import { createContext, useContext, useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { toast } from 'sonner';

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within WalletProvider');
  }
  return context;
};

export const WalletProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [balance, setBalance] = useState('0');
  const [connecting, setConnecting] = useState(false);

  // Treasury wallet address (RouteFlow fee collection)
  const TREASURY_ADDRESS = '0xed5fbb392fc77666444b9ed162fe072674210477';

  // Supported stablecoins
  const SUPPORTED_TOKENS = {
    USDC: {
      polygon: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
      ethereum: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48'
    },
    USDT: {
      polygon: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
      ethereum: '0xdAC17F958D2ee523a2206206994597C13D831ec7'
    }
  };

  // Check if MetaMask is installed
  const isMetaMaskInstalled = () => {
    return typeof window !== 'undefined' && typeof window.ethereum !== 'undefined';
  };

  // Connect wallet
  const connectWallet = async () => {
    if (!isMetaMaskInstalled()) {
      toast.error('Please install MetaMask to use crypto payments');
      window.open('https://metamask.io/download/', '_blank');
      return;
    }

    setConnecting(true);
    try {
      const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const network = await web3Provider.getNetwork();
      const web3Signer = web3Provider.getSigner();

      setProvider(web3Provider);
      setSigner(web3Signer);
      setAccount(accounts[0]);
      setChainId(network.chainId);

      // Get balance
      const balance = await web3Provider.getBalance(accounts[0]);
      setBalance(ethers.utils.formatEther(balance));

      toast.success('Wallet connected successfully!');
    } catch (error) {
      console.error('Error connecting wallet:', error);
      toast.error('Failed to connect wallet');
    } finally {
      setConnecting(false);
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setChainId(null);
    setBalance('0');
    toast.info('Wallet disconnected');
  };

  // Switch to Polygon network
  const switchToPolygon = async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x89' }], // Polygon Mainnet
      });
    } catch (switchError) {
      // If network doesn't exist, add it
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: '0x89',
                chainName: 'Polygon Mainnet',
                nativeCurrency: {
                  name: 'MATIC',
                  symbol: 'MATIC',
                  decimals: 18,
                },
                rpcUrls: ['https://polygon-rpc.com/'],
                blockExplorerUrls: ['https://polygonscan.com/'],
              },
            ],
          });
        } catch (addError) {
          toast.error('Failed to add Polygon network');
          throw addError;
        }
      } else {
        throw switchError;
      }
    }
  };

  // Send USDC payment
  const sendUSDCPayment = async (amount, recipient, token = 'USDC') => {
    if (!signer) {
      toast.error('Please connect your wallet first');
      return null;
    }

    try {
      // Ensure we're on Polygon
      if (chainId !== 137) {
        toast.info('Switching to Polygon network...');
        await switchToPolygon();
      }

      const tokenAddress = SUPPORTED_TOKENS[token].polygon;
      
      // USDC/USDT ERC20 ABI (simplified)
      const erc20ABI = [
        'function transfer(address to, uint amount) returns (bool)',
        'function balanceOf(address owner) view returns (uint)',
        'function decimals() view returns (uint8)'
      ];

      const tokenContract = new ethers.Contract(tokenAddress, erc20ABI, signer);
      
      // Check balance
      const balance = await tokenContract.balanceOf(account);
      const decimals = await tokenContract.decimals();
      const amountInWei = ethers.utils.parseUnits(amount.toString(), decimals);

      if (balance.lt(amountInWei)) {
        toast.error(`Insufficient ${token} balance`);
        return null;
      }

      // Calculate fee (0.48%)
      const feePercentage = 0.0048;
      const feeAmount = Math.floor(parseFloat(amount) * feePercentage * (10 ** decimals));
      const netAmount = amountInWei.sub(feeAmount);

      // Send fee to treasury
      toast.info('Sending payment...');
      const feeTx = await tokenContract.transfer(TREASURY_ADDRESS, feeAmount);
      await feeTx.wait();

      // Send net amount to recipient
      const tx = await tokenContract.transfer(recipient, netAmount);
      const receipt = await tx.wait();

      toast.success('Payment sent successfully!');
      
      return {
        hash: receipt.transactionHash,
        amount: amount,
        fee: feeAmount / (10 ** decimals),
        netAmount: ethers.utils.formatUnits(netAmount, decimals),
        token: token
      };
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment failed: ' + error.message);
      return null;
    }
  };

  // Listen to account changes
  useEffect(() => {
    if (isMetaMaskInstalled()) {
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
          disconnectWallet();
        } else {
          setAccount(accounts[0]);
        }
      });

      window.ethereum.on('chainChanged', (chainId) => {
        window.location.reload();
      });
    }
  }, []);

  const value = {
    account,
    provider,
    signer,
    chainId,
    balance,
    connecting,
    isConnected: !!account,
    connectWallet,
    disconnectWallet,
    switchToPolygon,
    sendUSDCPayment,
    TREASURY_ADDRESS,
    SUPPORTED_TOKENS
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};
